import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Users, Search, Mail, Phone, Settings2, X, Upload, Camera } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { talentApi, staffApi, rolesApi, departmentsApi } from "@/lib/unifiedApi";
import { ALL_USERS } from "@/lib/activityLog";
import { saveUserProfile, getUserProfile, getAllUserProfiles } from "@/lib/attendance";
import ExportDialog from "@/components/ExportDialog";
import { storageService } from "@/lib/storage";
import { toast } from "sonner";

// Default options (used as fallback if no custom options exist)
const DEFAULT_ROLES = ["Model", "Photographer", "Stylist", "Makeup Artist", "Creative Director", "Manager"];
const DEFAULT_DEPARTMENTS = ["General", "Creative", "Production", "Marketing", "Operations"];

const TEAM_EXPORT_COLUMNS = [
  { key: 'name',        label: 'Name' },
  { key: 'role',        label: 'Role' },
  { key: 'department',  label: 'Department' },
  { key: 'email',       label: 'Email' },
  { key: 'phone',       label: 'Phone' },
  { key: 'status',      label: 'Status' },
  { key: 'hourly_rate', label: 'Hourly Rate (฿)' },
];

const TeamPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // user_profiles map: userId → profile data
  const [profilesMap, setProfilesMap] = useState({});

  // Dynamic roles and departments from Firebase
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [newRole, setNewRole] = useState("");
  const [newDepartment, setNewDepartment] = useState("");
  const [editingRole, setEditingRole] = useState(null);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [editRoleValue, setEditRoleValue] = useState("");
  const [editDepartmentValue, setEditDepartmentValue] = useState("");
  
  const [formData, setFormData] = useState({
    name: "",
    role: "",
    email: "",
    phone: "",
    department: "",
    status: "active",
    hourly_rate: 0,
    avatar: "",
    userId: "",
    start_date: "",
    probation_months: 3,
  });

  const statuses = ["active", "inactive", "on_leave"];

  // Export filters
  const [exportFilters, setExportFilters] = useState({ status: 'all', department: 'all', role: 'all' });
  const exportData = items
    .filter((item) => {
      if (exportFilters.status !== 'all' && item.status !== exportFilters.status) return false;
      if (exportFilters.department !== 'all' && item.department !== exportFilters.department) return false;
      if (exportFilters.role !== 'all' && item.role !== exportFilters.role) return false;
      return true;
    })
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  useEffect(() => {
    // Clean up stale localStorage keys from the old implementation
    ['removed_roles', 'edited_roles', 'removed_departments', 'edited_departments'].forEach(
      (key) => localStorage.removeItem(key)
    );

    // Load user_profiles for linked display
    getAllUserProfiles().then(profiles => {
      const map = {};
      profiles.forEach(p => { if (p.userId) map[p.userId] = p; });
      setProfilesMap(map);
    }).catch(() => {});

    // Real-time subscription for team members
    // Auto-sync: create bizops_talent entry for any ALL_USERS member not yet in Firestore
    let synced = false;
    const unsubscribeTeam = talentApi.subscribe(async (data) => {
      setItems(data);
      setLoading(false);
      if (synced) return;
      synced = true;
      const existingUserIds = new Set(data.map(d => d.userId).filter(Boolean));
      for (const u of ALL_USERS) {
        if (existingUserIds.has(u.id)) continue;
        // Entry with same name but no userId yet → patch userId instead of creating duplicate
        const nameMatch = data.find(d => !d.userId && d.name?.toLowerCase() === u.name.toLowerCase());
        if (nameMatch) {
          await talentApi.update(nameMatch.id, { ...nameMatch, userId: u.id }).catch(() => {});
        } else {
          const entry = { userId: u.id, name: u.name, role: u.role === 'admin' ? 'Admin' : 'Staff', email: '', phone: '', department: '', status: 'active', hourly_rate: 0, avatar: '' };
          await talentApi.create(entry).catch(() => {});
          await staffApi.create({ userId: u.id, name: u.name, role: entry.role, email: '' }).catch(() => {});
        }
      }
    });

    // Real-time subscription for roles — seed defaults if collection is empty
    const unsubscribeRoles = rolesApi.subscribe(async (data) => {
      if (data.length === 0) {
        try {
          await Promise.all(
            DEFAULT_ROLES.map((name, idx) =>
              rolesApi.create({ name, order: idx })
            )
          );
          // Subscription fires again once seeded
        } catch (err) {
          console.error("Failed to seed roles:", err);
          setRoles(DEFAULT_ROLES.map((name, idx) => ({ id: `mem-${idx}`, name })));
        }
      } else {
        setRoles(data.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
      }
    });

    // Real-time subscription for departments — seed defaults if collection is empty
    const unsubscribeDepartments = departmentsApi.subscribe(async (data) => {
      if (data.length === 0) {
        try {
          await Promise.all(
            DEFAULT_DEPARTMENTS.map((name, idx) =>
              departmentsApi.create({ name, order: idx })
            )
          );
          // Subscription fires again once seeded
        } catch (err) {
          console.error("Failed to seed departments:", err);
          setDepartments(DEFAULT_DEPARTMENTS.map((name, idx) => ({ id: `mem-${idx}`, name })));
        }
      } else {
        setDepartments(data.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
      }
    });

    const timeout = setTimeout(() => setLoading(false), 3000);

    return () => {
      unsubscribeTeam();
      unsubscribeRoles();
      unsubscribeDepartments();
      clearTimeout(timeout);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await talentApi.update(editingItem.id, formData);
        // Sync staff dropdown
        try {
          const staffData = { name: formData.name, role: formData.role, email: formData.email };
          await staffApi.update(editingItem.id, staffData);
        } catch {
          await staffApi.create({ name: formData.name, role: formData.role, email: formData.email });
        }
        // Sync phone + employment info back to user_profiles if linked
        if (formData.userId) {
          try {
            const existing = await getUserProfile(formData.userId) || {};
            await saveUserProfile(formData.userId, {
              ...existing,
              phone: formData.phone,
              start_date: formData.start_date || "",
              probation_months: Number(formData.probation_months) || 3,
            });
          } catch {}
        }
        toast.success("Team member updated successfully");
      } else {
        await talentApi.create(formData);
        await staffApi.create({ name: formData.name, role: formData.role, email: formData.email });
        toast.success("Team member added successfully");
      }
      resetForm();
    } catch (error) {
      console.error("Error saving team member:", error);
      toast.error("Failed to save team member");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this team member?")) return;
    try {
      await talentApi.delete(id);
      toast.success("Team member deleted successfully");
    } catch (error) {
      console.error("Error deleting team member:", error);
      toast.error("Failed to delete team member");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    const prof = item.userId ? profilesMap[item.userId] : null;
    setFormData({
      name: item.name,
      role: item.role,
      email: item.email,
      phone: item.phone || "",
      department: item.department,
      status: item.status,
      hourly_rate: item.hourly_rate,
      avatar: item.avatar || "",
      userId: item.userId || "",
      start_date: (prof && prof.start_date) || "",
      probation_months: (prof && prof.probation_months != null) ? prof.probation_months : 3,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      name: "",
      role: "",
      email: "",
      phone: "",
      department: "",
      status: "active",
      hourly_rate: 0,
      avatar: "",
      userId: "",
      start_date: "",
      probation_months: 3,
    });
    setDialogOpen(false);
  };

  // Role management (pure Firestore — subscription keeps state in sync)
  const handleAddRole = async () => {
    if (!newRole.trim()) return;
    if (roles.some(r => r.name.toLowerCase() === newRole.trim().toLowerCase())) {
      toast.error("Role already exists");
      return;
    }
    try {
      await rolesApi.create({ name: newRole.trim(), order: roles.length });
      setNewRole("");
      toast.success("Role added");
    } catch (error) {
      console.error("Error adding role:", error);
      toast.error("Failed to add role");
    }
  };

  const handleUpdateRole = async (id) => {
    if (!editRoleValue.trim()) return;
    try {
      await rolesApi.update(id, { name: editRoleValue.trim() });
      toast.success("Role updated");
    } catch (error) {
      console.error("Error updating role:", error);
      toast.error("Failed to update role");
    }
    setEditingRole(null);
    setEditRoleValue("");
  };

  const handleDeleteRole = async (id) => {
    if (!window.confirm("Delete this role?")) return;
    try {
      await rolesApi.delete(id);
      toast.success("Role deleted");
    } catch (error) {
      console.error("Error deleting role:", error);
      toast.error("Failed to delete role");
    }
  };

  // Department management (pure Firestore — subscription keeps state in sync)
  const handleAddDepartment = async () => {
    if (!newDepartment.trim()) return;
    if (departments.some(d => d.name.toLowerCase() === newDepartment.trim().toLowerCase())) {
      toast.error("Department already exists");
      return;
    }
    try {
      await departmentsApi.create({ name: newDepartment.trim(), order: departments.length });
      setNewDepartment("");
      toast.success("Department added");
    } catch (error) {
      console.error("Error adding department:", error);
      toast.error("Failed to add department");
    }
  };

  const handleUpdateDepartment = async (id) => {
    if (!editDepartmentValue.trim()) return;
    try {
      await departmentsApi.update(id, { name: editDepartmentValue.trim() });
      toast.success("Department updated");
    } catch (error) {
      console.error("Error updating department:", error);
      toast.error("Failed to update department");
    }
    setEditingDepartment(null);
    setEditDepartmentValue("");
  };

  const handleDeleteDepartment = async (id) => {
    if (!window.confirm("Delete this department?")) return;
    try {
      await departmentsApi.delete(id);
      toast.success("Department deleted");
    } catch (error) {
      console.error("Error deleting department:", error);
      toast.error("Failed to delete department");
    }
  };

  // Picture upload handler
  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    setUploading(true);
    try {
      const result = await storageService.uploadFile(file, "team-photos", () => {});
      setFormData({ ...formData, avatar: result.url });
      toast.success("Photo uploaded");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload photo - check Firebase permissions");
    } finally {
      setUploading(false);
    }
  };

  const filteredItems = items.filter(
    (item) =>
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.department?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const styles = {
      active: "bg-emerald-50 text-emerald-700 border-emerald-200",
      inactive: "bg-gray-50 text-gray-500 border-gray-200",
      on_leave: "bg-amber-50 text-amber-700 border-amber-200",
    };
    return styles[status] || styles.active;
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Get role/department names for dropdowns
  const roleNames = roles.map(r => r.name);
  const departmentNames = departments.map(d => d.name);

  if (loading) {
    return (
      <div className="page-container" data-testid="team-loading">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#BFFF00] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="team-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Team</h1>
          <p className="text-gray-500 mt-1">Manage your team members</p>
        </div>
        <div className="flex gap-2 items-center">
          <ExportDialog
            title="Export Team"
            filename="team"
            data={exportData}
            columns={TEAM_EXPORT_COLUMNS}
            recordLabel="members"
          >
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-gray-700 text-sm">Status</Label>
                <Select value={exportFilters.status} onValueChange={(v) => setExportFilters((f) => ({ ...f, status: v }))}>
                  <SelectTrigger className="border-gray-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="all">All statuses</SelectItem>
                    {statuses.map((s) => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-gray-700 text-sm">Department</Label>
                <Select value={exportFilters.department} onValueChange={(v) => setExportFilters((f) => ({ ...f, department: v }))}>
                  <SelectTrigger className="border-gray-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="all">All departments</SelectItem>
                    {departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-gray-700 text-sm">Role</Label>
                <Select value={exportFilters.role} onValueChange={(v) => setExportFilters((f) => ({ ...f, role: v }))}>
                  <SelectTrigger className="border-gray-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="all">All roles</SelectItem>
                    {roles.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </ExportDialog>

          {/* Settings Button */}
          <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                data-testid="team-settings-btn"
                className="border-gray-200"
              >
                <Settings2 size={18} className="mr-2" />
                Roles & Departments
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200 max-w-xl">
              <DialogHeader>
                <DialogTitle className="text-gray-900">Manage Roles & Departments</DialogTitle>
              </DialogHeader>
              <Tabs defaultValue="roles" className="mt-4">
                <TabsList className="grid w-full grid-cols-2 bg-gray-100">
                  <TabsTrigger value="roles" className="data-[state=active]:bg-white">Roles</TabsTrigger>
                  <TabsTrigger value="departments" className="data-[state=active]:bg-white">Departments</TabsTrigger>
                </TabsList>
                
                {/* Roles Tab */}
                <TabsContent value="roles" className="space-y-4 mt-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add new role..."
                      value={newRole}
                      onChange={(e) => setNewRole(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleAddRole()}
                      className="border-gray-200"
                      data-testid="new-role-input"
                    />
                    <Button 
                      onClick={handleAddRole}
                      className="bg-[#BFFF00] hover:bg-[#a6e600] text-black"
                      data-testid="add-role-btn"
                    >
                      <Plus size={18} />
                    </Button>
                  </div>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {roles.map((role) => (
                      <div key={role.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                        {editingRole === role.id ? (
                          <>
                            <Input
                              value={editRoleValue}
                              onChange={(e) => setEditRoleValue(e.target.value)}
                              className="flex-1 border-gray-200 h-8"
                              autoFocus
                            />
                            <Button size="sm" onClick={() => handleUpdateRole(role.id)} className="h-8 bg-[#BFFF00] hover:bg-[#a6e600] text-black">
                              Save
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { setEditingRole(null); setEditRoleValue(""); }} className="h-8">
                              <X size={14} />
                            </Button>
                          </>
                        ) : (
                          <>
                            <span className="flex-1 text-gray-700">{role.name}</span>
                            <button
                              onClick={() => { setEditingRole(role.id); setEditRoleValue(role.name); }}
                              className="p-1.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600"
                              data-testid={`edit-role-${role.id}`}
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              onClick={() => handleDeleteRole(role.id)}
                              className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
                              data-testid={`delete-role-${role.id}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </TabsContent>

                {/* Departments Tab */}
                <TabsContent value="departments" className="space-y-4 mt-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add new department..."
                      value={newDepartment}
                      onChange={(e) => setNewDepartment(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleAddDepartment()}
                      className="border-gray-200"
                      data-testid="new-department-input"
                    />
                    <Button 
                      onClick={handleAddDepartment}
                      className="bg-[#BFFF00] hover:bg-[#a6e600] text-black"
                      data-testid="add-department-btn"
                    >
                      <Plus size={18} />
                    </Button>
                  </div>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {departments.map((dept) => (
                      <div key={dept.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                        {editingDepartment === dept.id ? (
                          <>
                            <Input
                              value={editDepartmentValue}
                              onChange={(e) => setEditDepartmentValue(e.target.value)}
                              className="flex-1 border-gray-200 h-8"
                              autoFocus
                            />
                            <Button size="sm" onClick={() => handleUpdateDepartment(dept.id)} className="h-8 bg-[#BFFF00] hover:bg-[#a6e600] text-black">
                              Save
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { setEditingDepartment(null); setEditDepartmentValue(""); }} className="h-8">
                              <X size={14} />
                            </Button>
                          </>
                        ) : (
                          <>
                            <span className="flex-1 text-gray-700">{dept.name}</span>
                            <button
                              onClick={() => { setEditingDepartment(dept.id); setEditDepartmentValue(dept.name); }}
                              className="p-1.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600"
                              data-testid={`edit-dept-${dept.id}`}
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              onClick={() => handleDeleteDepartment(dept.id)}
                              className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
                              data-testid={`delete-dept-${dept.id}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>

          {/* Add Team Member Button */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-team-btn"
                className="bg-[#BFFF00] hover:bg-[#a6e600] text-black font-medium rounded-full"
                onClick={() => resetForm()}
              >
                <Plus size={18} className="mr-2" />
                Add Team Member
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-gray-900">
                  {editingItem ? "Edit Team Member" : "Add New Team Member"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Photo Upload */}
                <div className="flex items-center gap-4">
                  <div className="relative">
                    {formData.avatar ? (
                      <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-[#BFFF00]">
                        <img 
                          src={formData.avatar} 
                          alt="Staff" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
                        <Camera size={24} className="text-gray-400" />
                      </div>
                    )}
                    <label className="absolute bottom-0 right-0 p-1.5 bg-[#BFFF00] rounded-full cursor-pointer hover:bg-[#a6e600] transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePictureUpload}
                        className="hidden"
                        disabled={uploading}
                      />
                      {uploading ? (
                        <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Upload size={14} className="text-black" />
                      )}
                    </label>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-700">Profile Photo</p>
                    <p className="text-xs text-gray-500">Upload a photo (max 5MB)</p>
                    {formData.avatar && (
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, avatar: "" })}
                        className="text-xs text-red-500 hover:underline mt-1"
                      >
                        Remove photo
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-700">Name</Label>
                  <Input
                    data-testid="team-name-input"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="Full name"
                    required
                    className="border-gray-200"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Role</Label>
                    <Select
                      value={formData.role}
                      onValueChange={(v) => setFormData({ ...formData, role: v })}
                    >
                      <SelectTrigger
                        data-testid="team-role-select"
                        className="border-gray-200"
                      >
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        {roleNames.length === 0 ? (
                          <SelectItem value="_none" disabled>No roles available</SelectItem>
                        ) : (
                          roleNames.map((r) => (
                            <SelectItem key={r} value={r}>
                              {r}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Department</Label>
                    <Select
                      value={formData.department}
                      onValueChange={(v) =>
                        setFormData({ ...formData, department: v })
                      }
                    >
                      <SelectTrigger
                        data-testid="team-department-select"
                        className="border-gray-200"
                      >
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        {departmentNames.length === 0 ? (
                          <SelectItem value="_none" disabled>No departments available</SelectItem>
                        ) : (
                          departmentNames.map((d) => (
                            <SelectItem key={d} value={d}>
                              {d}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Email</Label>
                  <Input
                    data-testid="team-email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    placeholder="email@example.com"
                    required
                    className="border-gray-200"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Phone</Label>
                    <Input
                      data-testid="team-phone-input"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                      placeholder="+1 234 567 8900"
                      className="border-gray-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Hourly Rate (฿)</Label>
                    <Input
                      data-testid="team-rate-input"
                      type="number"
                      step="0.01"
                      value={formData.hourly_rate}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          hourly_rate: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="border-gray-200"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(v) => setFormData({ ...formData, status: v })}
                  >
                    <SelectTrigger
                      data-testid="team-status-select"
                      className="border-gray-200"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {statuses.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s.replace("_", " ")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">วันเริ่มงาน</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="border-gray-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">ทดลองงาน (เดือน)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={formData.probation_months}
                      onChange={(e) => setFormData({ ...formData, probation_months: e.target.value })}
                      className="border-gray-200"
                    />
                  </div>
                </div>
                {!formData.userId && (
                  <p className="text-[11px] text-amber-600">* วันเริ่มงานจะบันทึกเมื่อสมาชิกเชื่อมกับบัญชีผู้ใช้ (login) แล้วเท่านั้น</p>
                )}
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetForm}
                    className="flex-1 border-gray-200"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    data-testid="team-submit-btn"
                    className="flex-1 bg-[#BFFF00] hover:bg-[#a6e600] text-black"
                  >
                    {editingItem ? "Update" : "Add Team Member"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          size={18}
        />
        <Input
          data-testid="team-search"
          placeholder="Search team..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 border-gray-200"
        />
      </div>

      {/* Grid */}
      {filteredItems.length === 0 ? (
        <Card className="bg-white border-gray-100 shadow-sm rounded-2xl">
          <CardContent className="p-12 text-center">
            <Users className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No team members found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredItems.map((item) => (
            <Card
              key={item.id}
              data-testid={`team-card-${item.id}`}
              className="bg-white border-gray-100 shadow-sm rounded-2xl hover:border-gray-200 transition-colors"
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  {item.avatar ? (
                    <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[#BFFF00]/30 flex-shrink-0">
                      <img 
                        src={item.avatar} 
                        alt={item.name} 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-[#BFFF00]/20 border border-[#BFFF00]/30 flex items-center justify-center text-gray-900 font-semibold flex-shrink-0">
                      {getInitials(item.name)}
                    </div>
                  )}

                  {/* Info */}
                  {(() => {
                    const p = item.userId ? profilesMap[item.userId] : null;
                    const realName = p && (p.firstName || p.lastName) ? `${p.firstName || ''} ${p.lastName || ''}`.trim() : null;
                    const displayPhone = p?.phone || item.phone;
                    return (
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {realName || item.name}
                        </h3>
                        {realName && realName !== item.name && (
                          <span className="text-[10px] text-gray-400">({item.name})</span>
                        )}
                        <span
                          className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${getStatusBadge(
                            item.status
                          )}`}
                        >
                          {item.status?.replace("_", " ")}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{item.role}</p>
                      <p className="text-xs text-gray-400 mt-1">{item.department}</p>

                      <div className="flex flex-wrap gap-3 mt-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Mail size={12} />
                          {p?.email || item.email}
                        </span>
                        {displayPhone && (
                          <span className="flex items-center gap-1">
                            <Phone size={12} />
                            {displayPhone}
                          </span>
                        )}
                      </div>

                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                      <span className="text-sm text-gray-600">
                        ฿{item.hourly_rate}/hr
                      </span>
                      <div className="flex gap-1">
                        <button
                          data-testid={`edit-team-${item.id}`}
                          onClick={() => handleEdit(item)}
                          className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          data-testid={`delete-team-${item.id}`}
                          onClick={() => handleDelete(item.id)}
                          className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamPage;
